import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import DonutChart from '../../components/common/Chart/DonutChart';
import AuditPipelineOverview from '../../components/audit/AuditPipelineOverview';
import { filterExcludedAuditTasks } from '../../utils/auditTaskUtils';
import { buildAuditPipelineStats } from '../../utils/auditPipelineUtils';
import {
  Users, FolderOpen, FileText, BarChart3, TrendingUp,
  ClipboardList, CheckCircle, RefreshCw, Building2, Briefcase,
  ChevronRight, Target, Activity, Percent, Award,
  AlertCircle, Clock, Shield, GraduationCap, UserCheck, UserX, Zap,
  PauseCircle, XCircle, Ban
} from 'lucide-react';

// ── Brand palette (teal — matches sidebar #1f656c) ─────────────────────────
const T = {
  dark:    '#0d3d42',
  primary: '#1f656c',
  mid:     '#2a8a93',
  light:   '#4db8c0',
  tint10:  'rgba(31,101,108,0.10)',
  tint15:  'rgba(31,101,108,0.15)',
  tint20:  'rgba(31,101,108,0.20)',
};

// ── CRM accent colors (harmonised with teal) ────────────────────────────────
const CRM_CARDS = [
  { label: 'Companies',  key: 'totalCompany',   icon: Building2,  color: T.primary, bg: T.tint10,                       to: '/company' },
  { label: 'Leads',      key: 'totalLeads',     icon: Users,      color: '#2563eb', bg: 'rgba(37,99,235,0.10)',          to: '/leads' },
  { label: 'Proposals',  key: 'totalProposals', icon: FileText,   color: '#0ca678', bg: 'rgba(12,166,120,0.10)',         to: '/proposals' },
  { label: 'Projects',   key: 'totalProjects',  icon: FolderOpen, color: '#d97706', bg: 'rgba(217,119,6,0.10)',          to: '/project' },
];

const FUNNEL_COLORS = [T.primary, '#2563eb', '#0ca678', '#16a34a', '#d97706'];

const MODULE_CARDS = [
  { label: 'CRM Dashboard',  icon: BarChart3,    color: T.primary,  to: '/crm-dashboard',        desc: 'Sales funnel & metrics',   moduleKey: 'crm-dashboard'   },
  { label: 'Audit Platform', icon: ClipboardList,color: '#16a34a',  to: '/audit-dashboard',       desc: 'Project pipeline',         moduleKey: 'audit-dashboard' },
  { label: 'Leads',          icon: Users,        color: '#2563eb',  to: '/leads',                 desc: 'Manage prospects',         moduleKey: 'leads'           },
  { label: 'Projects',       icon: FolderOpen,   color: '#d97706',  to: '/project',               desc: 'Track projects',           moduleKey: 'project'         },
  { label: 'Proposals',      icon: FileText,     color: '#0ca678',  to: '/proposals',             desc: 'Proposal management',      moduleKey: 'proposal'        },
  { label: 'Sales Report',   icon: TrendingUp,   color: '#7c3aed',  to: '/reports/sales-report',  desc: 'Revenue & performance',    moduleKey: 'sales-report'    },
];

const parseCount = (value) => { const n = Number(value); return Number.isFinite(n) ? n : null; };
const pickCount = (stats, keys) => { for (const key of keys) { const p = parseCount(stats?.[key]); if (p != null) return p; } return 0; };
const normalizeCrmStats = (stats = {}) => ({
  ...stats,
  totalCompany:   pickCount(stats, ['totalCompany','totalCompanies','totalProspect','totalProspects','prospectTotal','companyCount','companies']),
  totalLeads:     pickCount(stats, ['totalLeads','leads','leadCount']),
  totalProposals: pickCount(stats, ['totalProposals','convertedToProposal','totalProposal','proposalCount','proposals']),
  totalProjects:  pickCount(stats, ['totalProjects','projects','projectCount','convertedToProject']),
});

// ── Skeleton ───────────────────────────────────────────────────────────────
const Skeleton = ({ w = 'col-4', h = 20 }) => (
  <span className={`placeholder d-block rounded ${w}`} style={{ height: h }} />
);

// ── Section Header ─────────────────────────────────────────────────────────
const SectionHeader = ({ icon: Icon, color, title, btnLabel, onBtnClick }) => (
  <div className="d-flex align-items-center justify-content-between mb-3">
    <div className="d-flex align-items-center gap-2">
      <div className="rounded-2 d-flex align-items-center justify-content-center"
        style={{ width: 32, height: 32, background: `${color}1a` }}>
        <Icon size={16} color={color} />
      </div>
      <h2 className="h5 mb-0 fw-semibold" style={{ color: '#1a2e35' }}>{title}</h2>
    </div>
    {btnLabel && (
      <button className="btn btn-sm d-flex align-items-center gap-1"
        style={{ color: color, border: `1px solid ${color}40`, borderRadius: 6, fontSize: '0.78rem', padding: '3px 10px', background: `${color}08` }}
        onClick={onBtnClick}>
        {btnLabel} <ChevronRight size={13} />
      </button>
    )}
  </div>
);

// ── KPI Card ───────────────────────────────────────────────────────────────
const KpiCard = ({ label, value, icon: Icon, color, bg, loading, onClick }) => (
  <div className="col-6 col-md-3" role={onClick ? 'button' : undefined} onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default' }}>
    <div className="card h-100 border-0 shadow-sm" style={{ borderTop: `3px solid ${color}`, transition: 'transform .15s,box-shadow .15s' }}
      onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 8px 24px ${color}22`; }}}
      onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
      <div className="card-body d-flex align-items-center gap-3 py-3">
        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 46, height: 46, background: bg }}>
          <Icon size={21} color={color} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="text-muted text-truncate" style={{ fontSize: '0.73rem' }}>{label}</div>
          {loading
            ? <div className="placeholder-glow mt-1"><Skeleton w="col-6" h={26} /></div>
            : <div className="fw-bold mt-1" style={{ fontSize: '1.5rem', lineHeight: 1, color }}>{(value ?? 0).toLocaleString()}</div>
          }
        </div>
      </div>
    </div>
  </div>
);

// ── Sales Funnel Viz ───────────────────────────────────────────────────────
const SalesFunnel = ({ data, categories, loading }) => {
  if (loading) return (
    <div className="d-flex flex-column gap-2">
      {[1,2,3,4,5].map(i => <div key={i} className="placeholder-glow"><Skeleton w="col-12" h={44} /></div>)}
    </div>
  );
  if (!data || !data.length) return (
    <div className="text-center text-muted py-4">
      <BarChart3 size={36} className="mb-2 opacity-25" />
      <div className="small">No funnel data available</div>
    </div>
  );
  const max = Math.max(...data, 1);
  return (
    <div className="d-flex flex-column gap-2">
      {categories.map((cat, i) => {
        const val = data[i] || 0;
        const pct = Math.round((val / max) * 100);
        const color = FUNNEL_COLORS[i] || '#999';
        return (
          <div key={cat}>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div className="rounded-circle flex-shrink-0 d-flex align-items-center justify-content-center text-white"
                style={{ width: 22, height: 22, background: color, fontSize: '0.6rem', fontWeight: 700 }}>
                {i + 1}
              </div>
              <span className="small fw-medium" style={{ minWidth: 90 }}>{cat}</span>
              {i < categories.length - 1 && data[i+1] > 0 && (
                <span style={{ fontSize: '0.68rem', color: '#adb5bd' }}>
                  → {Math.round((data[i+1]/Math.max(val,1))*100)}% conv.
                </span>
              )}
              <span className="ms-auto fw-bold small" style={{ color }}>{val.toLocaleString()}</span>
            </div>
            <div className="progress" style={{ height: 7, borderRadius: 4, background: 'rgba(0,0,0,.05)' }}>
              <div className="progress-bar" style={{
                width: `${pct}%`, background: `linear-gradient(90deg,${color}88,${color})`,
                borderRadius: 4, transition: 'width .8s ease'
              }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Insight Card ───────────────────────────────────────────────────────────
const InsightCard = ({ label, value, subtitle, icon: Icon, color, bg, loading }) => (
  <div className="col-12 col-sm-4">
    <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body d-flex align-items-center gap-3 py-3">
        <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
          style={{ width: 44, height: 44, background: bg }}>
          <Icon size={20} color={color} />
        </div>
        <div>
          {loading
            ? <div className="placeholder-glow"><Skeleton w="col-8" h={26} /></div>
            : <div className="fw-bold" style={{ fontSize: '1.55rem', lineHeight: 1, color }}>{value}</div>
          }
          <div className="fw-semibold small mt-1" style={{ color: '#1a2e35' }}>{label}</div>
          <div className="text-muted" style={{ fontSize: '0.69rem' }}>{subtitle}</div>
        </div>
      </div>
    </div>
  </div>
);

// ── Main Component ─────────────────────────────────────────────────────────
const CombinedDashboard = () => {
  const navigate = useNavigate();
  const { user, can, hasRole, capabilitiesLoading } = useAuth();

  // ── Permission gates ────────────────────────────────────────────────────
  const isAdmin   = hasRole('ROLE_ADMIN');
  const showCrm   = isAdmin || can('view_own', 'crm-dashboard') || can('view_global', 'crm-dashboard');
  const showAudit = isAdmin || can('view_own', 'audit-dashboard') || can('view_global', 'audit-dashboard');
  const canSeeModule = (key) => isAdmin || can('view_own', key) || can('view_global', key);

  // Dynamic subtitle
  const platformLabel = showCrm && showAudit ? 'CRM & Audit overview in one view'
    : showCrm   ? 'CRM overview'
    : showAudit ? 'Audit overview'
    : 'CBQA Global Platform';

  const [crmStats, setCrmStats]         = useState(null);
  const [auditTasks, setAuditTasks]     = useState([]);
  const [funnelData, setFunnelData]     = useState(null);
  const [proposalData, setProposalData] = useState(null);
  const [certCompStats, setCertCompStats] = useState(null);
  const [loading, setLoading]           = useState(true);
  const [refreshing, setRefreshing]     = useState(false);
  const [lastUpdated, setLastUpdated]   = useState(null);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    const promises = [
      showCrm  ? api.get('/dashboard/statistics/total').catch(() => ({ data: {} }))   : Promise.resolve({ data: {} }),
      showAudit ? api.get('/audit/tasks').catch(() => ({ data: [] }))                  : Promise.resolve({ data: [] }),
      showCrm  ? api.get('/dashboard/sales-funnel?period=all_time').catch(() => ({ data: {} }))       : Promise.resolve({ data: {} }),
      showCrm  ? api.get('/dashboard/proposal-breakdown?period=all_time').catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
    ];
    const [statsRes, tasksRes, funnelRes, proposalRes] = await Promise.all(promises);
    setCrmStats(normalizeCrmStats(statsRes.data || {}));
    setAuditTasks(filterExcludedAuditTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []));
    setFunnelData(funnelRes.data || null);
    setProposalData(proposalRes.data || null);
    setLastUpdated(new Date());
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    // Wait for capabilities to resolve before fetching (avoids double-fetch for non-admin)
    if (!capabilitiesLoading) { fetchData(); }
  }, [capabilitiesLoading, showCrm, showAudit]);

  useEffect(() => {
    if (!capabilitiesLoading && showAudit) {
      api.get('/audit/dashboard/cert-competency-stats')
        .then(res => setCertCompStats(res.data || null))
        .catch(() => {});
    }
  }, [capabilitiesLoading, showAudit]);

  const audit = buildAuditPipelineStats(auditTasks);
  const displayName = user?.username || user?.name || 'User';
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const propSeries = proposalData?.series || [];
  const propLabels = proposalData?.labels || [];
  const hasPropData = propSeries.some(v => v > 0);

  const funnelValues = funnelData?.data || [];
  const funnelCats   = funnelData?.categories || [];

  const leads       = crmStats?.totalLeads    || 0;
  const proposals   = crmStats?.totalProposals || 0;
  const projects    = crmStats?.totalProjects  || 0;
  const convRate    = leads > 0 ? Math.round((proposals / leads) * 100) : 0;
  const auditComp   = audit.total > 0 ? Math.round((audit.counts.Completed / audit.total) * 100) : 0;
  const activePipeline = leads + proposals + projects;

  return (
    <>
      {/* Show spinner while capabilities are loading (non-admin users) */}
      {capabilitiesLoading && !isAdmin && (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border" style={{ color: T.primary }} />
        </div>
      )}

      {(!capabilitiesLoading || isAdmin) && (
      <div className="container-xl pb-4">

        {/* ── Hero Header ──────────────────────────────────────────────── */}
        <div className="rounded-3 mb-4 p-4 p-md-5 text-white position-relative overflow-hidden"
          style={{ background: `linear-gradient(135deg, ${T.dark} 0%, ${T.primary} 55%, ${T.mid} 100%)` }}>
          {/* decorative circles */}
          <div className="position-absolute top-0 end-0"
            style={{ width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', transform: 'translate(30%,-30%)' }} />
          <div className="position-absolute bottom-0 start-0"
            style={{ width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', transform: 'translate(-30%,30%)' }} />
          <div className="position-absolute" style={{ top: '50%', right: '8%', width: 140, height: 140, borderRadius: '50%', border: '40px solid rgba(255,255,255,0.06)', transform: 'translateY(-50%)' }} />

          <div className="row align-items-center position-relative">
            <div className="col">
              <div className="mb-1" style={{ opacity: .65, fontSize: '0.82rem', letterSpacing: '.02em' }}>{dateStr}</div>
              <h1 className="mb-1 fw-bold text-white" style={{ fontSize: 'clamp(1.35rem,4vw,1.95rem)', letterSpacing: '-.01em' }}>
                Welcome, {displayName} 👋
              </h1>
              <p className="mb-0" style={{ opacity: .75, fontSize: '0.88rem' }}>
                CBQA Global Platform — {platformLabel}
              </p>
            </div>
            <div className="col-auto d-flex align-items-center gap-2 mt-3 mt-md-0">
              {lastUpdated && (
                <span style={{ opacity: .55, fontSize: '0.78rem' }}>
                  {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
              <button className="btn btn-sm d-flex align-items-center gap-1"
                style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, backdropFilter: 'blur(4px)' }}
                onClick={() => fetchData(true)} disabled={refreshing}>
                <RefreshCw size={13} className={refreshing ? 'spin-anim' : ''} />
                {refreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* ── CRM KPI Cards ─────────────────────────────────────────────── */}
        {showCrm && (<>
        <SectionHeader icon={Briefcase} color={T.primary} title="CRM Overview"
          btnLabel="Full Dashboard" onBtnClick={() => navigate('/crm-dashboard')} />
        <div className="row g-3 mb-4">
          {CRM_CARDS.map(({ label, key, icon, color, bg, to }) => (
            <KpiCard key={key} label={label} icon={icon} color={color} bg={bg}
              value={crmStats?.[key] ?? 0} loading={loading} onClick={() => navigate(to)} />
          ))}
        </div>

        {/* ── CRM Charts: Sales Funnel + Proposal Breakdown ─────────────── */}
        <div className="row g-3 mb-4">
          {/* Sales Funnel */}
          <div className="col-12 col-md-7">
            <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid ${T.primary}` }}>
              <div className="card-header bg-transparent border-0 d-flex align-items-center justify-content-between pb-0">
                <div className="d-flex align-items-center gap-2">
                  <TrendingUp size={15} color={T.primary} />
                  <h3 className="card-title fw-semibold mb-0" style={{ fontSize: '0.95rem' }}>Sales Funnel</h3>
                </div>
                <button className="btn btn-sm d-flex align-items-center gap-1"
                  style={{ color: T.primary, fontSize: '0.73rem', padding: '2px 8px', background: T.tint10, border: 'none', borderRadius: 6 }}
                  onClick={() => navigate('/crm-dashboard')}>
                  Detail <ChevronRight size={11} />
                </button>
              </div>
              <div className="card-body pt-2">
                <SalesFunnel data={funnelValues} categories={funnelCats} loading={loading} />
              </div>
            </div>
          </div>

          {/* Proposal Status DonutChart */}
          <div className="col-12 col-md-5">
            <div className="card border-0 shadow-sm h-100" style={{ borderTop: `3px solid #0ca678` }}>
              <div className="card-header bg-transparent border-0 d-flex align-items-center justify-content-between pb-0">
                <div className="d-flex align-items-center gap-2">
                  <FileText size={15} color="#0ca678" />
                  <h3 className="card-title fw-semibold mb-0" style={{ fontSize: '0.95rem' }}>Proposal Status</h3>
                </div>
                <button className="btn btn-sm d-flex align-items-center gap-1"
                  style={{ color: '#0ca678', fontSize: '0.73rem', padding: '2px 8px', background: 'rgba(12,166,120,0.08)', border: 'none', borderRadius: 6 }}
                  onClick={() => navigate('/proposals')}>
                  View <ChevronRight size={11} />
                </button>
              </div>
              <div className="card-body d-flex flex-column align-items-center justify-content-center pt-1">
                {loading ? (
                  <div className="py-5"><div className="spinner-border opacity-50" style={{ color: T.primary }} /></div>
                ) : hasPropData ? (
                  <>
                    <DonutChart series={propSeries} labels={propLabels} height="220"
                      legendPosition="bottom" type="type_distinct"
                      onDataPointClick={() => navigate('/proposals')} />
                    <div className="text-muted mt-1" style={{ fontSize: '0.68rem', opacity: 0.65 }}>
                      Click a slice to open Proposals
                    </div>
                  </>
                ) : (
                  <div className="text-center text-muted py-4">
                    <FileText size={36} className="mb-2 opacity-25" />
                    <div className="small">No proposal data yet</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        </>)}

        {/* ── Audit Platform Section ──────────────────────────────────────── */}
        {showAudit && (<>
        <SectionHeader icon={ClipboardList} color="#16a34a" title="Audit Platform Overview"
          btnLabel="Full Dashboard" onBtnClick={() => navigate('/audit-dashboard')} />
        <div className="row g-3 mb-4">
          {/* Total card */}
          <div className="col-12 col-sm-6 col-md-4 col-lg-3">
            <div className="card border-0 shadow-sm h-100"
              style={{ background: `linear-gradient(135deg, ${T.dark} 0%, ${T.primary} 100%)` }}>
              <div className="card-body text-white d-flex flex-column justify-content-center py-4">
                <CheckCircle size={30} className="mb-2" style={{ opacity: 0.7 }} />
                <div style={{ fontSize: '2.4rem', fontWeight: 700, lineHeight: 1 }}>
                  {loading
                    ? <span className="placeholder col-4 bg-light opacity-25 rounded" style={{ height: 38 }} />
                    : audit.total}
                </div>
                <div className="mt-1" style={{ opacity: .8, fontSize: '0.88rem' }}>Total Audit Projects</div>
                <div className="mt-2 pt-2 border-top border-white border-opacity-25 d-flex gap-3">
                  <div>
                    <div className="fw-bold">{loading ? '—' : audit.active}</div>
                    <div style={{ fontSize: '0.7rem', opacity: .75 }}>Active</div>
                  </div>
                  <div>
                    <div className="fw-bold">{loading ? '—' : audit.counts.Completed}</div>
                    <div style={{ fontSize: '0.7rem', opacity: .75 }}>Completed</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shared pipeline — same data contract and UI as Audit Dashboard */}
          <div className="col-12 col-sm-6 col-md-8 col-lg-9">
            <AuditPipelineOverview
              counts={audit.counts}
              total={audit.total}
              loading={loading}
              onStageClick={() => navigate('/audit-dashboard')}
            />
          </div>
        </div>
        </>)}

        {/* ── Certificate Health + Competency Mapping ──────────────────── */}
        {showAudit && certCompStats && (
        <div className="row g-3 mb-4">
          {/* Certificate Health */}
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Shield size={17} color="#f59e0b" />
                    </div>
                    <span className="fw-semibold" style={{ color: '#1a2e35', fontSize: '0.95rem' }}>Certificate Health</span>
                  </div>
                  <button type="button" className="btn btn-sm"
                    style={{ color: '#f59e0b', border: '1px solid #fed7aa', borderRadius: 8, backgroundColor: '#fff7ed', fontSize: '0.75rem', fontWeight: 600 }}
                    onClick={() => navigate('/audit/certificate-monitoring')}>
                    View all →
                  </button>
                </div>
                <div className="row g-2 mb-3">
                  {[
                    { label: 'Total Certificates', value: certCompStats.certificates?.total ?? 0, color: '#0ea5e9', bg: '#f0f9ff', bgIcon: '#e0f2fe', icon: CheckCircle },
                    { label: 'Active',              value: certCompStats.certificates?.active ?? 0, color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7', icon: CheckCircle },
                    { label: 'Expiring Soon',       value: certCompStats.certificates?.expiringSoon ?? 0, color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7', icon: Clock },
                    { label: 'Expired',             value: certCompStats.certificates?.expired ?? 0, color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2', icon: AlertCircle },
                  ].map(({ label, value, color, bg, bgIcon, icon: Icon }) => (
                    <div className="col-6" key={label}>
                      <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ backgroundColor: bg }}>
                        <div style={{ width: 36, height: 36, borderRadius: 7, backgroundColor: bgIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={17} color={color} />
                        </div>
                        <div>
                          <div className="fw-bold" style={{ fontSize: '1.3rem', color, lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{label}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {(() => {
                  const total = certCompStats.certificates?.total || 1;
                  const activeW  = Math.round(((certCompStats.certificates?.active || 0) / total) * 100);
                  const expW     = Math.round(((certCompStats.certificates?.expiringSoon || 0) / total) * 100);
                  const expiredW = Math.max(0, 100 - activeW - expW);
                  return (
                    <div>
                      <div className="d-flex justify-content-between mb-1">
                        <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 500 }}>Health Overview</span>
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{certCompStats.certificates?.total ?? 0} total</span>
                      </div>
                      <div style={{ display: 'flex', height: 8, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
                        {activeW > 0 && <div style={{ flex: activeW, backgroundColor: '#22c55e' }} />}
                        {expW > 0    && <div style={{ flex: expW,     backgroundColor: '#f59e0b' }} />}
                        {expiredW > 0 && <div style={{ flex: expiredW, backgroundColor: '#ef4444' }} />}
                      </div>
                      <div className="d-flex gap-3 mt-2">
                        {[['#22c55e','Active'],['#f59e0b','Expiring Soon'],['#ef4444','Expired']].map(([c, l]) => (
                          <span key={l} style={{ fontSize: '0.65rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
                            <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: c, display: 'inline-block' }} />{l}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* Competency Mapping */}
          <div className="col-12 col-lg-6">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: '#f5f3ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <GraduationCap size={17} color="#7c3aed" />
                    </div>
                    <span className="fw-semibold" style={{ color: '#1a2e35', fontSize: '0.95rem' }}>Competency Mapping</span>
                  </div>
                  <button type="button" className="btn btn-sm"
                    style={{ color: '#7c3aed', border: '1px solid #ddd6fe', borderRadius: 8, backgroundColor: '#f5f3ff', fontSize: '0.75rem', fontWeight: 600 }}
                    onClick={() => navigate('/audit/competency-management')}>
                    View all →
                  </button>
                </div>
                <div className="row g-2 mb-3">
                  {[
                    { label: 'Total Imported', value: certCompStats.competencies?.total ?? 0,     color: '#0ea5e9', bg: '#f0f9ff', bgIcon: '#e0f2fe', icon: Users },
                    { label: 'Matched',        value: certCompStats.competencies?.matched ?? 0,   color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7', icon: UserCheck },
                    { label: 'Auto-created',   value: certCompStats.competencies?.created ?? 0,   color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9', icon: Zap },
                    { label: 'Unmatched',      value: certCompStats.competencies?.unmatched ?? 0, color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7', icon: UserX },
                  ].map(({ label, value, color, bg, bgIcon, icon: Icon }) => (
                    <div className="col-6" key={label}>
                      <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ backgroundColor: bg }}>
                        <div style={{ width: 36, height: 36, borderRadius: 7, backgroundColor: bgIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={17} color={color} />
                        </div>
                        <div>
                          <div className="fw-bold" style={{ fontSize: '1.3rem', color, lineHeight: 1 }}>{value}</div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{label}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {certCompStats.qualificationBreakdown?.length > 0 && (
                  <div>
                    <div className="fw-semibold mb-2" style={{ fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                      Personnel Qualification Distribution
                    </div>
                    {certCompStats.qualificationBreakdown.slice(0, 6).map(({ name, count }) => {
                      const maxCount = certCompStats.qualificationBreakdown[0]?.count || 1;
                      const pct = Math.round((count / maxCount) * 100);
                      return (
                        <div key={name} className="mb-1 d-flex align-items-center gap-2">
                          <span style={{ fontSize: '0.72rem', color: '#374151', minWidth: 150, maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                          <div className="flex-grow-1" style={{ height: 6, borderRadius: 3, backgroundColor: '#f1f5f9', overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, height: '100%', backgroundColor: '#7c3aed', borderRadius: 3, transition: 'width .6s ease' }} />
                          </div>
                          <span style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ── Certificate Lifecycle Status (auditqv2-style) ────────────────
            Additive: does not replace or alter the Certificate Health card above.
            Only renders once at least one certificate has been imported with a
            lifecycle status (i.e. via a live AuditQ import) — orgs that haven't
            imported yet see no empty/all-zero widget. */}
        {showAudit && (
          Object.keys(certCompStats?.certificates?.byLifecycleStatus?.iso || {}).length > 0
          || Object.keys(certCompStats?.certificates?.byLifecycleStatus?.iscc || {}).length > 0
        ) && (
        <div className="row g-3 mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 12 }}>
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <div style={{ width: 34, height: 34, borderRadius: 8, backgroundColor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Shield size={17} color="#f59e0b" />
                    </div>
                    <span className="fw-semibold" style={{ color: '#1a2e35', fontSize: '0.95rem' }}>Certificate Lifecycle Status</span>
                  </div>
                  <button type="button" className="btn btn-sm"
                    style={{ color: '#f59e0b', border: '1px solid #fed7aa', borderRadius: 8, backgroundColor: '#fff7ed', fontSize: '0.75rem', fontWeight: 600 }}
                    onClick={() => navigate('/audit/certificate-monitoring')}>
                    View all →
                  </button>
                </div>

                {Object.keys(certCompStats?.certificates?.byLifecycleStatus?.iso || {}).length > 0 && (
                <div className="mb-3">
                  <div className="fw-semibold mb-2" style={{ fontSize: '0.72rem', color: '#475569' }}>ISO</div>
                  {/* CSS grid, not a Bootstrap flex row: a flex column (col-6 col-lg) can shrink
                      below its content's natural width, forcing labels like "Discontinue Not
                      Active" to wrap one character per line. minmax(150px, 1fr) guarantees a
                      floor: tiles wrap to fewer per row instead of ever going narrower than they
                      can legibly render. */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                    {[
                      { label: 'Active', color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7', icon: CheckCircle },
                      { label: 'Suspended', color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7', icon: PauseCircle },
                      { label: 'Withdrawn', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9', icon: XCircle },
                      { label: 'Discontinue Not Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2', icon: Ban },
                      { label: 'Discontinue Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2', icon: Ban },
                    ].map(({ label, color, bg, bgIcon, icon: Icon }) => (
                      <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ backgroundColor: bg }} key={label}>
                        <div style={{ width: 36, height: 36, borderRadius: 7, backgroundColor: bgIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={17} color={color} />
                        </div>
                        <div className="min-w-0">
                          <div className="fw-bold" style={{ fontSize: '1.3rem', color, lineHeight: 1 }}>
                            {certCompStats.certificates?.byLifecycleStatus?.iso?.[label] ?? 0}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {Object.keys(certCompStats?.certificates?.byLifecycleStatus?.iscc || {}).length > 0 && (
                <div>
                  <div className="fw-semibold mb-2" style={{ fontSize: '0.72rem', color: '#475569' }}>ISCC</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
                    {[
                      { label: 'Valid', color: '#22c55e', bg: '#f0fdf4', bgIcon: '#dcfce7', icon: CheckCircle },
                      { label: 'Suspended', color: '#f59e0b', bg: '#fffbeb', bgIcon: '#fef3c7', icon: PauseCircle },
                      { label: 'Terminate', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9', icon: XCircle },
                      { label: 'Discontinue Not Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2', icon: Ban },
                      { label: 'Withdrawn', color: '#64748b', bg: '#f8fafc', bgIcon: '#f1f5f9', icon: XCircle },
                      { label: 'Discontinue Active', color: '#ef4444', bg: '#fef2f2', bgIcon: '#fee2e2', icon: Ban },
                    ].map(({ label, color, bg, bgIcon, icon: Icon }) => (
                      <div className="d-flex align-items-center gap-2 p-2 rounded-3" style={{ backgroundColor: bg }} key={label}>
                        <div style={{ width: 36, height: 36, borderRadius: 7, backgroundColor: bgIcon, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon size={17} color={color} />
                        </div>
                        <div className="min-w-0">
                          <div className="fw-bold" style={{ fontSize: '1.3rem', color, lineHeight: 1 }}>
                            {certCompStats.certificates?.byLifecycleStatus?.iscc?.[label] ?? 0}
                          </div>
                          <div style={{ fontSize: '0.68rem', color: '#64748b' }}>{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ── Key Insights Strip ────────────────────────────────────────── */}
        {(showCrm || showAudit) && (<>
        <SectionHeader icon={Activity} color={T.primary} title="Key Insights" />
        <div className="row g-3 mb-4">
          {showCrm && (
          <InsightCard label="Conversion Rate" value={`${convRate}%`}
            subtitle="Proposals created from Leads"
            icon={Percent} color={T.primary} bg={T.tint10} loading={loading} />
          )}
          {showAudit && (
          <InsightCard label="Audit Completion" value={`${auditComp}%`}
            subtitle="Projects completed out of total"
            icon={CheckCircle} color="#16a34a" bg="rgba(22,163,74,0.1)" loading={loading} />
          )}
          {(showCrm || showAudit) && (
          <InsightCard label="Active Pipeline" value={activePipeline.toLocaleString()}
            subtitle="Leads + Proposals + Projects"
            icon={Target} color="#d97706" bg="rgba(217,119,6,0.1)" loading={loading} />
          )}
        </div>
        </>)}

        {/* ── Quick Access ───────────────────────────────────────────────── */}
        <SectionHeader icon={Award} color={T.primary} title="Quick Access" />
        <div className="row g-3 pb-2">
          {MODULE_CARDS.filter(({ moduleKey }) => canSeeModule(moduleKey)).map(({ label, icon: Icon, color, to, desc }) => (
            <div key={label} className="col-6 col-md-4 col-lg-2">
              <div className="card border-0 shadow-sm h-100 text-center"
                style={{ cursor: 'pointer', transition: 'transform .15s,box-shadow .15s', borderTop: `3px solid ${color}` }}
                onClick={() => navigate(to)}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow=`0 8px 24px ${color}28`; }}
                onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}>
                <div className="card-body py-4 d-flex flex-column align-items-center">
                  <div className="rounded-3 mb-3 d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48, background: `${color}15` }}>
                    <Icon size={22} color={color} />
                  </div>
                  <div className="fw-semibold small mb-1" style={{ fontSize: '0.82rem' }}>{label}</div>
                  <div className="text-muted" style={{ fontSize: '0.68rem' }}>{desc}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
      )}

      <style>{`
        .spin-anim { animation: _spin 1s linear infinite; }
        @keyframes _spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
};

export default CombinedDashboard;
